import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const API_BASE = '/api/v1';

// Raw fetch without auth — we add auth in the wrapper
const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE,
  credentials: 'include',
});

// ═══════════════════════════════════════════════════════════════
//  TOAST DEDUPLICATION — prevent identical error toasts flooding the UI
//
//  Problem: When Redis/MongoDB drops, every API call (10+ concurrent on
//  dashboard load, plus TimerWidget polling every 30s) returns the same
//  500 "Connection is closed." error → 10+ identical toasts pile up.
//
//  Solution: Throttle by message — suppress duplicates within a cooldown window.
// ═══════════════════════════════════════════════════════════════
const _recentToasts = new Map(); // message_key → timestamp
const TOAST_COOLDOWN_MS = 8000;  // suppress same message for 8 seconds

// Keywords that indicate infrastructure issues (not user-actionable)
const _CONNECTION_KEYWORDS = [
  'connection is closed', 'connection close', 'econnrefused', 'econnreset',
  'socket hang up', 'network error', 'failed to fetch', 'enotfound',
  'etimedout', 'econnaborted', 'redis', 'mongo', 'topology was destroyed',
  'buffering timed out', 'server selection timed out', 'unavailable',
];

/**
 * Detect if an error message is an infrastructure / connection problem
 * (i.e., not something the user can fix by changing their input).
 */
function _isConnectionError(msg) {
  const lower = (msg || '').toLowerCase();
  return _CONNECTION_KEYWORDS.some((kw) => lower.includes(kw));
}

/**
 * Show a toast only if the same message hasn't been shown recently.
 * Prevents 10+ identical "Connection is closed." toasts stacking up.
 */
async function _throttledToast(message, options = {}) {
  const key = message.toLowerCase().trim();
  const now = Date.now();

  // Already showed this message recently? Skip.
  if (_recentToasts.has(key) && now - _recentToasts.get(key) < TOAST_COOLDOWN_MS) {
    return;
  }
  _recentToasts.set(key, now);

  // Housekeeping: purge stale entries to prevent memory leak
  if (_recentToasts.size > 30) {
    for (const [k, ts] of _recentToasts) {
      if (now - ts > TOAST_COOLDOWN_MS * 3) _recentToasts.delete(k);
    }
  }

  try {
    const { toast } = await import('react-hot-toast');
    toast.error(message, options);
  } catch (_) { /* toast import failed — skip */ }
}

/**
 * Custom baseQuery that picks the right auth token based on URL:
 * - /client-portal/c/*  → clientAccessToken (client portal user)
 * - /client-portal/manage/* → accessToken (admin/team user)
 * - everything else → accessToken (team user)
 */
/**
 * Current CSRF token from the cookie.
 *
 * Must be re-read after any token refresh: the server rotates this cookie in
 * auth.controller.refreshToken via setCsrfCookie(). A retry that reuses the
 * token captured before the refresh sends a stale header against a fresh
 * cookie, and the double-submit check rejects it with
 * "CSRF token validation failed. Please refresh the page and try again."
 */
const readCsrfToken = () => document.cookie
  .split('; ')
  .find((c) => c.startsWith('csrf-token='))
  ?.split('=')[1];

const baseQueryWithAuth = async (args, api, extraOptions) => {
  const url = typeof args === 'string' ? args : args?.url || '';
  const isClientPortalClientRoute = url.includes('/client-portal/c/') || url.includes('/client-portal/auth/');
  const isClientPortalRoute = url.includes('/client-portal/');

  // Inject the correct auth header
  const clientToken = localStorage.getItem('clientAccessToken');
  const teamToken = localStorage.getItem('accessToken');

  const headers = {};
  if (isClientPortalClientRoute && clientToken) {
    headers['Authorization'] = `Bearer ${clientToken}`;
  } else if (teamToken) {
    headers['Authorization'] = `Bearer ${teamToken}`;
  }

  const workspaceId = localStorage.getItem('activeWorkspaceId');
  if (workspaceId) headers['X-Workspace-Id'] = workspaceId;

  // ── CSRF token (read from cookie, send in header — double-submit pattern) ──
  const csrfToken = readCsrfToken();
  if (csrfToken) headers['x-csrf-token'] = csrfToken;

  // Merge headers into args
  const adjustedArgs = typeof args === 'string'
    ? { url: args, headers }
    : { ...args, headers: { ...args.headers, ...headers } };

  let result = await rawBaseQuery(adjustedArgs, api, extraOptions);

  // Handle 401 → auto-refresh → retry
  if (result?.error?.status === 401) {
    if (isClientPortalClientRoute) {
      const refreshResult = await rawBaseQuery(
        { url: '/client-portal/auth/refresh', method: 'POST' },
        api, extraOptions,
      );
      if (refreshResult?.data?.data?.accessToken) {
        localStorage.setItem('clientAccessToken', refreshResult.data.data.accessToken);
        adjustedArgs.headers['Authorization'] = `Bearer ${refreshResult.data.data.accessToken}`;
        const rotatedCsrf = readCsrfToken();
        if (rotatedCsrf) adjustedArgs.headers['x-csrf-token'] = rotatedCsrf;
        result = await rawBaseQuery(adjustedArgs, api, extraOptions);
      } else {
        localStorage.removeItem('clientAccessToken');
        localStorage.removeItem('clientUser');
        window.location.href = '/client-portal/login';
      }
    } else {
      // Send stored refresh token in body as fallback for dev proxy (cookie may not forward)
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const refreshResult = await rawBaseQuery(
        { url: '/auth/refresh', method: 'POST', body: storedRefreshToken ? { refreshToken: storedRefreshToken } : {} },
        api, extraOptions,
      );
      if (refreshResult?.data?.data?.accessToken) {
        localStorage.setItem('accessToken', refreshResult.data.data.accessToken);
        // Store rotated refresh token if returned
        if (refreshResult.data.data.refreshToken) {
          localStorage.setItem('refreshToken', refreshResult.data.data.refreshToken);
        }
        adjustedArgs.headers['Authorization'] = `Bearer ${refreshResult.data.data.accessToken}`;
        // The refresh rotated the CSRF cookie too. Retrying with the token we
        // captured before the refresh is a guaranteed 403 — this is what made
        // the first action after an access-token expiry fail with
        // "CSRF token validation failed", on whatever screen you happened
        // to be using at the time.
        const rotatedCsrf = readCsrfToken();
        if (rotatedCsrf) adjustedArgs.headers['x-csrf-token'] = rotatedCsrf;
        result = await rawBaseQuery(adjustedArgs, api, extraOptions);
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('activeWorkspaceId');
        if (!isClientPortalRoute) {
          try {
            const { toast } = await import('react-hot-toast');
            toast.error('Session expired. Please log in again.', { duration: 4000 });
          } catch {}
          setTimeout(() => { window.location.href = '/login'; }, 1500);
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  GLOBAL ERROR TOASTS — deduplicated, connection-aware
  // ═══════════════════════════════════════════════════════════════
  if (result?.error && result.error.status !== 401) {
    const errMsg = result.error.data?.error?.message || result.error.data?.error?.message || '';
    const errCode = result.error.data?.error?.code || '';
    const status = result.error.status;
    const fetchError = result.error.error || ''; // RTK Query FETCH_ERROR string

    // ── Network / infrastructure errors (FETCH_ERROR, 502, 503, Redis/Mongo down) ──
    // These are NOT user-actionable — show a single friendly message, heavily throttled.
    if (
      status === 'FETCH_ERROR' ||
      _isConnectionError(errMsg) ||
      _isConnectionError(fetchError) ||
      errCode === 'REDIS_UNAVAILABLE'
    ) {
      _throttledToast('Connection issue — retrying automatically', { icon: '🔌', duration: 5000 });
      return result;
    }

    // ── 503 Service Unavailable ──
    if (status === 503) {
      _throttledToast(errMsg || 'Service temporarily unavailable', { icon: '🔌', duration: 5000 });
    }
    // ── 403 Forbidden (various sub-codes) ──
    else if (status === 403) {
      if (errCode === 'PLAN_LIMIT_REACHED' || errCode === 'FEATURE_NOT_AVAILABLE') {
        _throttledToast(errMsg || 'Upgrade your plan to access this feature', { icon: '🔒', duration: 5000 });
      } else if (errCode === 'PORTAL_DISABLED' || errCode === 'FEATURE_DISABLED') {
        _throttledToast(errMsg || 'This feature is not enabled', { icon: '⚙️', duration: 4000 });
      } else if (errCode === 'MEMBER_LIMIT_REACHED' || errCode === 'PROJECT_LIMIT_REACHED') {
        _throttledToast(errMsg || 'Limit reached — upgrade your plan', { icon: '📊', duration: 5000 });
      } else if (errCode === 'INSUFFICIENT_ROLE' || errCode === 'PERMISSION_DENIED') {
        _throttledToast(errMsg || 'You don\'t have permission for this action', { icon: '🚫', duration: 4000 });
      } else {
        _throttledToast(errMsg || 'Access denied', { duration: 4000 });
      }
    }
    // ── 404 Not Found (only on mutations — skip polling/cache misses) ──
    else if (status === 404) {
      const method = typeof args === 'string' ? 'GET' : (args?.method || 'GET');
      if (method !== 'GET') {
        _throttledToast(errMsg || 'Not found', { duration: 3000 });
      }
    }
    // ── 409 Conflict ──
    else if (status === 409) {
      _throttledToast(errMsg || 'Conflict — item already exists', { icon: '⚠️', duration: 4000 });
    }
    // ── 413 Payload Too Large ──
    else if (status === 413) {
      _throttledToast(errMsg || 'File too large', { icon: '📁', duration: 4000 });
    }
    // ── 429 Rate Limited ──
    else if (status === 429) {
      _throttledToast('Too many requests — please slow down', { icon: '⏳', duration: 4000 });
    }
    // ── 5xx Server Errors ──
    else if (status >= 500) {
      // Don't leak raw internal error messages to the user.
      // Show the server message only if it looks user-friendly (short, no stack traces).
      const isFriendly = errMsg && errMsg.length < 120 && !errMsg.includes('\n') && !errMsg.includes('at ');
      _throttledToast(isFriendly ? errMsg : 'Server error — please try again', { icon: '💥', duration: 4000 });
    }
    // ── 400 Bad Request ──
    else if (status === 400) {
      _throttledToast(errMsg || 'Invalid request', { duration: 3000 });
    }
  }

  return result;
};

// The single API instance — all module API slices inject into this
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  // Tag types for cache invalidation across modules
  tagTypes: [
    // ── Registered in bulk after discovering 86 tag types were used by
    // providesTags/invalidatesTags but never declared here. RTK Query
    // silently ignores an unknown tag, so every one of those invalidations
    // was a no-op: mutations succeeded and the lists they should have
    // refreshed simply did not. Deleting a Studio agent was the symptom;
    // accounts, budgets, compliance and the whole super-admin surface had
    // the same defect.
    'AISettings',
    'QuickActions',
    'AccContact',
    'AccInvoice',
    'AccJournal',
    'AccNote',
    'AccPayment',
    'AccProduct',
    'AccReports',
    'AccountsDashboard',
    'AccountsSettings',
    'Activity',
    'ApiToken',
    'UserIntegration',
    'Addons',
    'Agent',
    'AgentAction',
    'AgentConversation',
    'AgentKnowledge',
    'AgentSchedule',
    'AgentTemplate',
    'AgentVersion',
    'ApprovalConfig',
    'ApprovalRequest',
    'BankTxn',
    'BillingHistory',
    'Breach',
    'BudgetAlerts',
    'BudgetScenario',
    'BudgetTemplate',
    'BudgetVersion',
    'CapexOpex',
    'ClientAnalytics',
    'ClientFinancial',
    'ClientPayment',
    'ClientProforma',
    'ClientQuotation',
    'CoA',
    'Compliance',
    'Consent',
    'DeptBudget',
    'DocTemplate',
    'EvalSuite',
    'Evidence',
    'ExpenseAnalytics',
    'ExpensePolicy',
    'FinCalendar',
    'GuardrailLog',
    'HLCanvas',
    'HLCanvasTemplate',
    /* Poll was used by five poll mutations and never declared here, so every one
       of those invalidations was silently dropped — the list only refreshed when
       something else happened to invalidate 'Message'. */
    'Poll',
    'HLAISettings',
    'DeepWork',
    'HLInviteTemplate',
    'IntegrationDash',
    'PMSettings',
    'PMStats',
    'Proforma',
    'Quotation',
    'RevenueRecog',
    'SAAddon',
    'SAAddons',
    'SAAlerts',
    'SAAnalytics',
    'SACoupons',
    'SADashboard',
    'SAEntitlements',
    'SAEvents',
    'SALineItems',
    'SAMe',
    'SAMeters',
    'SAPayment',
    'SAPayments',
    'SAPlan',
    'SAPlans',
    'SAPlatformConfig',
    'SAPromotions',
    'SASubscribers',
    'SASubscription',
    'SATaxRates',
    'SAUsage',
    'SAWallets',
    'SAWebhook',
    'SAWebhookDelivery',
    'SlackChannelMap',
    'SlackChannels',
    'SprintHealth',
    'Standup',
    'StockTxn',
    'StudioAnalytics',
    'Subscription',
    'Templates',
    'ZunoActions',
    'ZunoAgent',
    'ZunoRisks',

    // F4 — evaluation harness
    'EvalRun',
    'User',
    'Workspace',
    'Project',
    'ProjectRisk',
    'ProjectIssue',
    'ProjectDecision',
    'ProjectCR',
    'ProjectLesson',
    'ProjectRaci',
    'ProjectPhase',
    'ProjectSnapshot',
    'Task',
    'TaskStats',
    'TaskStatuses',
    'TaskTemplate',
    'TaskGraph',
    'TaskApproval',
    'TaskRelation',
    'TaskAutomation',
    'TaskReaction',
    'PlanningPoker',
    'ProjectTemplate',
    'MeetingRoom',
    'SavedView',
    'Sprint',
    'SprintPlanning',
    'SprintStandup',
    'SprintReview',
    'SprintAnalytics',
    'RetroBoard',
    'Milestone',
    'MilestoneTemplate',
    'Channel',
    'Message',
    'TimeLog',
    'TimeEntry',
    'Timesheet',
    'Budget',
    'Goal',
    'Document',
    'Report',
    'Workflow',
    'AIUsage',
    'AIConversation',
    'Notification',
    'Resource',
    'Call',
    'SharedChannel',
    'SavedReply',
    'Emoji',
    'CalEvent',
    'Availability',
    'Booking',
    'KBDoc',
    'KBTemplate',
    'ClientDashboard',
    'ClientRoadmap',
    'ClientRequest',
    'ClientApproval',
    'ClientBudget',
    'ClientInvoice',
    'ClientMessage',
    'ClientTask',
    'ClientDocument',
    'ClientReport',
    'ClientCalendar',
    'ClientScheduling',
    'ClientKB',
    'ClientTimeLogs',
    'ClientMilestone',
    'ClientActivity',
    'ClientAI',
    'ClientSprint',
    'ClientGoal',
    'ClientNotification',
    'ClientForum',
    'ClientSurvey',
    'ClientUpload',
    'ClientChangelog',
    'ClientPrefs',
    'ClientMultiDash',
    'ManagedTeam',
    'Portfolio',
    'ClientConfig',
    'ClientActivityLog',
    'ClientBadges',
    'ClientTeamContacts',
    'ClientMeetings',
    'ClientBudgetAlerts',
    'ManagedApproval',
    'ManagedInvoice',
    'InvoiceSettings',
    'ManagedMessage',
    'ManagedOnboarding',
    'ClientMe',
    'PortalConfig',
    'ManagedClient',
    'ManagedRequest',
    'Members', 'ProjectContext', 'HuddleCompliance',
    'Employee',
    'EmployeeView',
    'EmployeeCustomField',
    'EmployeeTeam',
    'EmployeeAnalytics',
    'OrgChart',
    'OnboardingTemplate',
    'OnboardingProgress',
    'LeaveRequest',
    'LeaveBalance',
    'ReviewCycle',
    'PerformanceReview',
    'PeerFeedback',
    'LeaveSettings',
    'FeedbackSettings',
    'FeedbackRequest',
    'EmployeeDocument',
    'EmployeeTask',
    'Role',
    'UserPermissions',
    'MyPermissions',
    'Departments',
    'Billing',
    'Plans',
    'Security',
    'CustomFields',
    'AdminInsights',
    'Integration',
    'LinkedRepo',
    'AvailableRepos',
    'TaskGit',
    'ProjectGit',
  ],
  endpoints: () => ({}), // Empty — modules inject their own endpoints
});